import { apply, celebrate, onceToday } from '../core/effects';
import { S } from '../core/game';
import { m } from '../i18n';
import { sfx } from '../audio/sfx';
import { say as speak } from '../audio/speech';
import { drawCharacter, type CharLook } from '../render/sprites';
import { h } from './dom';
import { isPanelOpen, openPanel, refreshPanel } from './panel';
import { forecastBlock } from './skyPanels';
import { typhoonTomorrow, weatherFor } from '../content/weather';
import { dayNum } from '../content/calendar';

/** A news anchor of our own design. */
const ANCHOR: CharLook = { skin: '#f3cba6', hair: '#2a1d16', hairStyle: 'bun', top: '#2f4a8a', topShade: '#23386a', bottom: '#2f4a8a', shoes: '#1d1814', dress: true, badge: '#ffd34e' };

const state = { i: 0, answered: null as null | boolean, raf: 0 };

function todaysStories(): number[] {
  const s = S();
  const n = m().news.stories.length;
  const start = ((s.week - 1) * 3 + s.dayIndex) * 3;
  return [0, 1, 2].map((k) => (start + k) % n);
}

export function openNews(): void {
  state.i = 0;
  state.answered = null;
  openPanel('news', render, () => cancelAnimationFrame(state.raf));
  readCurrent();
}

function readCurrent(): void {
  const idx = todaysStories()[state.i];
  const story = m().news.stories[idx];
  if (story) speak(`${story.head}. ${story.body}`, { pitch: 1.05, rate: 1 });
}

function render() {
  const t = m().news;
  const ids = todaysStories();
  const story = t.stories[ids[state.i]];
  const cv = h('canvas', { class: 'news-cv', width: 560, height: 315 });
  cancelAnimationFrame(state.raf);
  const loop = (now: number) => {
    if (!isPanelOpen('news') || !cv.isConnected) return;
    paint(cv, now / 1000, story?.head ?? t.done);
    state.raf = requestAnimationFrame(loop);
  };
  state.raf = requestAnimationFrame(loop);

  // The weather forecast opens every bulletin (sky package).
  const fc = forecastBlock();
  if (!story) return { title: t.title, body: [cv, fc, h('p', { class: 'hint big' }, t.done)] };
  const answer = (realChoice: boolean) => {
    const right = realChoice === story.real;
    state.answered = right;
    sfx(right ? 'ok' : 'bad');
    if (right && onceToday(`news.${ids[state.i]}`)) {
      celebrate();
      apply({ stars: 3, study: 2 });
    }
    refreshPanel('news');
  };
  return {
    title: t.title,
    className: 'news',
    body: [
      cv,
      fc,
      h('h4', null, story.head),
      h('p', { class: 'news-body' }, story.body),
      state.answered === null
        ? [h('p', { class: 'hint' }, t.quiz), h('div', { class: 'grid2' }, h('button', { class: 'btn green', onclick: () => answer(true) }, t.real), h('button', { class: 'btn red', onclick: () => answer(false) }, t.fake))]
        : [
            h('div', { class: `lesson-card ${state.answered ? 'good' : 'bad'}` }, h('b', null, `${state.answered ? '✅' : '❌'} ${state.answered ? t.right : t.wrong} — ${story.real ? t.real : t.fake}`), h('p', null, story.why)),
            h(
              'button',
              {
                class: 'btn gold wide',
                onclick: () => {
                  state.i++;
                  state.answered = null;
                  readCurrent();
                  refreshPanel('news');
                },
              },
              t.nextStory,
            ),
          ],
    ],
  };
}

function paint(cv: HTMLCanvasElement, t: number, headline: string): void {
  const c = cv.getContext('2d')!;
  const W = cv.width, H = cv.height;
  c.setTransform(1, 0, 0, 1, 0, 0);
  // Studio: deep blue gradient with a city skyline and moving light sweeps.
  const g = c.createLinearGradient(0, 0, W, H);
  g.addColorStop(0, '#0f2a5a');
  g.addColorStop(1, '#2a6fb5');
  c.fillStyle = g;
  c.fillRect(0, 0, W, H);
  c.fillStyle = 'rgba(255,255,255,0.08)';
  for (let i = 0; i < 4; i++) {
    const x = ((t * 40 + i * 180) % (W + 200)) - 100;
    c.beginPath();
    c.moveTo(x, 0);
    c.lineTo(x + 60, 0);
    c.lineTo(x - 40, H);
    c.lineTo(x - 100, H);
    c.fill();
  }
  c.fillStyle = 'rgba(10,25,60,0.8)';
  for (let i = 0; i < 14; i++) {
    const bw = 30 + ((i * 37) % 25), bh = 50 + ((i * 53) % 90);
    c.fillRect(i * 42, H * 0.62 - bh, bw, bh);
    c.fillStyle = 'rgba(255,220,120,0.6)';
    for (let k = 0; k < 6; k++) c.fillRect(i * 42 + 5 + (k % 3) * 8, H * 0.62 - bh + 8 + Math.floor(k / 3) * 14, 3, 5);
    c.fillStyle = 'rgba(10,25,60,0.8)';
  }
  // Desk and anchor (drawn big).
  c.save();
  c.scale(4, 4);
  drawCharacter(c, ANCHOR, 30, 60, { facing: 'down', phase: 0, moving: false, blink: t % 4 < 0.12, breath: (t / 1.6) % 1 });
  c.restore();
  const dg = c.createLinearGradient(0, H * 0.6, 0, H * 0.75);
  dg.addColorStop(0, '#e8eef6');
  dg.addColorStop(1, '#9fb0c8');
  c.fillStyle = dg;
  c.fillRect(30, H * 0.62, 260, H * 0.14);
  c.fillStyle = '#d0453a';
  c.fillRect(30, H * 0.62, 260, 6);
  // LIVE badge.
  c.fillStyle = Math.sin(t * 4) > 0 ? '#e0332a' : '#b8221a';
  c.beginPath();
  c.roundRect(W - 96, 16, 80, 28, 6);
  c.fill();
  c.fillStyle = '#fff';
  c.font = '800 16px system-ui, sans-serif';
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillText(`● ${m().news.live}`, W - 56, 31);
  // Lower third with the headline, and a scrolling ticker.
  c.fillStyle = 'rgba(255,255,255,0.95)';
  c.fillRect(0, H - 78, W, 44);
  c.fillStyle = '#d0453a';
  c.fillRect(0, H - 78, 10, 44);
  c.fillStyle = '#10244a';
  c.textAlign = 'left';
  c.font = '800 17px system-ui, "PingFang TC", "Noto Sans TC", sans-serif';
  let size = 17;
  while (c.measureText(headline).width > W - 40 && size > 11) {
    size--;
    c.font = `800 ${size}px system-ui, "PingFang TC", "Noto Sans TC", sans-serif`;
  }
  c.fillText(headline, 22, H - 56);
  c.fillStyle = '#10244a';
  c.fillRect(0, H - 34, W, 34);
  c.fillStyle = '#ffd34e';
  c.font = '600 14px system-ui, "PingFang TC", "Noto Sans TC", sans-serif';
  // Typhoon tomorrow: the red breaking-news strip takes over the ticker.
  const alert = typhoonTomorrow();
  if (alert) {
    c.fillStyle = Math.sin(t * 5) > 0 ? '#c0281c' : '#a01f15';
    c.fillRect(0, H - 34, W, 34);
    c.fillStyle = '#fff3d0';
  }
  const wx = weatherFor(dayNum());
  const tick = alert ? m().sky.forecast.ticker : m().sky.forecast.tickerToday(m().sky.kinds[wx.kind].toLowerCase(), wx.lo, wx.hi, wx.rainPct);
  const ticker = `${tick}   ${tick}   `;
  const tw = c.measureText(ticker).width;
  const off = (t * 60) % (tw / 2);
  c.fillText(ticker, 10 - off, H - 16);
}
